import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@journal/shared";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Auth route shell.
 *
 * Daily Journal's auth no longer uses the shared `AuthLayout` (the 4-variant
 * glass-card-in-a-split-panel frame that made every clone look the same). Each
 * screen now renders its OWN `JournalAuthScene` — an editorial letter on a ruled
 * sheet (masthead + serif headline + full-bleed form, no card, no split) — so
 * the brand band and copy live with each screen, not in a shared layout. This
 * shell only owns route guarding and the page transition between screens.
 *
 * The scroll fix (#13) lives inside `JournalAuthScene`: `AppBackground` is a
 * fixed decorative sibling and the content scrolls in its own `min-h-[100dvh]
 * overflow-y-auto` container, so the form is always reachable on small viewports.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

/**
 * P23/CC-60 · I1-bis. Both helpers live in MODULE scope on purpose: they do not
 * capture anything from `beforeLoad`, and declaring them inside would make
 * `unicorn/consistent-function-scoping` fail (a `pnpm lint` error, with `tsc` passing
 * — the exact order in which it bites).
 */
const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });

type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Writes the response into the machine and cache as soon as it arrives, EVEN IF
 * the deadline has already expired and login is being displayed. This prevents the
 * `BootManager` from seeing a "pending" or stale session during the route
 * transition, which is what caused redirect loops.
 */
const syncReply = (reply: FetchResult): FetchResult => {
  const { data: replyData, error: replyError } = reply;
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here, otherwise the switcher's "Add account"
    // would bounce straight back to the app and you could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` CANNOT be unbounded. With `get-session`
    // STALLED (held, neither 401 nor 5xx: both resolve quickly and the `queryFn`
    // swallows them in a `?? null`), `beforeLoad` did not resolve, the router did
    // not mount the route, and `/login` stayed BLANK — measured here:
    // `#root.childElementCount === 0` at 3, 6, 10, and 15 s, and at 15 s the
    // anti-stale-PWA watchdog in `index.html` reloaded the page. The sibling
    // guard in `_app` ALREADY had a deadline; this one was left without one because the
    // value was a local literal, and that is why I1 was closed green by measuring `/`.
    //
    // The request is NOT canceled: it stays alive and syncs machine + cache when
    // it arrives (`syncReply`). If there turns out to be a session, `BootManager` takes the
    // user out of the login screen: you pay for a flicker, never an empty screen.
    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Deadline expired or network error: the login is DISPLAYED. The late response is already
      // hooked above and `BootManager` redirects if it contains a session.
      return;
    }

    const { data } = result;

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
    // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
    const sessionData = data as { user?: unknown } | null | undefined;
    if (sessionData?.user) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
